import { forwardRef } from 'react';
import type { ReactNode } from 'react';

export interface MediaFrameProps {
  /** The media itself — a `<video>`, `<img>`, or anything that fills the box. */
  children: ReactNode;
  /**
   * Aspect ratio of the media box, as a CSS `aspect-ratio` value.
   * Defaults to `8 / 5` — the ratio the product tour is mastered at.
   */
  ratio?: string;
  /** Short corner caption for the media — rendered as the figure's `<figcaption>`. */
  label?: string;
  /** Fades the bottom of the media into the frame's own glass surface. Decorative. */
  scrim?: boolean;
  /** Overlay control(s) pinned to the bottom-right of the media box (e.g. play/pause). */
  controls?: ReactNode;
  className?: string;
}

// ------------------------------------------------------------------
// Media frame — the glass mat a piece of product media sits on (P10 §2.3).
// Owns the aspect ratio, the rounded/clipped media box, the optional corner
// label and the optional bottom scrim; the media element itself stays the
// caller's, so this primitive never learns about <video> or <img>.
//
// a11y: the frame is chrome, so it announces nothing of its own — no role,
// no tabIndex, and the scrim is `aria-hidden` + `pointer-events-none` so it
// neither reaches the a11y tree nor swallows a click meant for the media.
// `label` is a real `<figcaption>` (a direct child of the `<figure>`), which
// is what associates it with the media it captions rather than leaving a
// floating div near it. Overlay text never depends on the footage for its
// contrast: the caption carries its own `--surface-contrast` chip with
// `text-foreground` (worst case over pure-black footage in light mode ≈15:1,
// over pure-white footage in dark mode ≈6.9:1), and the scrim only fades
// toward the frame's own surface, so it can lift contrast but never cut it.
//
// `group` is on the figure so overlay controls can reveal on `group-hover:` /
// `group-focus-visible:`; they must stay visible without hover on touch.
// ------------------------------------------------------------------
export const MediaFrame = forwardRef<HTMLElement, MediaFrameProps>(function MediaFrame(
  { children, ratio = '8 / 5', label, scrim = false, controls, className = '' },
  ref,
) {
  return (
    <figure ref={ref} className={`rounded-hairline border border-border bg-card group relative p-2 ${className}`}>
      <div className="relative overflow-hidden rounded-lg bg-muted" style={{ aspectRatio: ratio }}>
        {children}

        {scrim && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-card to-transparent"
          />
        )}

        {controls && <div className="absolute bottom-4 right-4">{controls}</div>}
      </div>

      {label && (
        <figcaption className="absolute bottom-6 left-6 max-w-[calc(100%-3rem)] truncate rounded-full border border-border bg-surface-contrast px-3 py-1.5 text-caption font-semibold uppercase tracking-eyebrow text-foreground backdrop-blur-md">
          {label}
        </figcaption>
      )}
    </figure>
  );
});
